import type { Realtime } from '@ably-labs/react-hooks';
import { configureAbly } from '@ably-labs/react-hooks';
import type { Types as AblyTypes } from 'ably';
import { useRef, useEffect, useState } from 'react';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { captureException } from 'hb-react/shared/utils/errorReporting';

export type AblyTokenType = 'frontoffice' | 'publicoffice' | 'backoffice';

type UseAblyAuthUrl = (tokenType: AblyTokenType) => string | null;

type UseAblyAvailable = (enabled?: boolean) => boolean;

interface UseSubscribeToAblyChannelParams {
  tokenType: AblyTokenType;
  channel: string;
  onMessage: AblyTypes.messageCallback<AblyTypes.Message>;
  enabled?: boolean;
  useHistory?: boolean;
}

export const clusterToEnv = (cluster: string) => {
  switch (cluster) {
    case 'euprod':
    case 'eusandbox':
    case 'staging':
      return 'eu';
    case 'usprod':
    case 'wip':
    case 'ussandbox':
      return 'us';
    default:
      return undefined; // Default case if none of the above cases match
  }
};

// Ably is not available in PO right now
export const useAblyAuthUrl: UseAblyAuthUrl = (tokenType) => {
  const { flagEvAblyWebsockets } = useFeatureFlag();

  const { currentAdmin, currentUser, currentNetworkId } =
    useDefaultAppContexts();

  if (!flagEvAblyWebsockets) {
    return null;
  }

  if (
    typeof Routes.backoffice_network_ably_token_path === 'function' &&
    (!currentAdmin || !currentNetworkId)
  ) {
    const message = `Trying to use Ably in the backoffice but missing currentAdmin and/or currentNetworkId: { currentAdmin: ${currentAdmin}, currentNetworkId: ${currentNetworkId} }`;

    // `Routes` is faked during tests, we cannot rely on typeof Routes.backoffice_network_ably_token_path === 'function' to check if we are in BO
    // so we don't throw an error in tests
    if (process.env.NODE_ENV !== 'test') {
      throw new Error(message);
    }
  }

  const inBO: boolean = !!(
    currentAdmin &&
    currentNetworkId &&
    typeof Routes.backoffice_network_ably_token_path === 'function'
  );
  const inFO: boolean = !!currentUser;

  if (inBO && currentNetworkId) {
    return Routes.backoffice_network_ably_token_path(currentNetworkId, {
      type: tokenType,
      format: 'json',
    });
  }

  if (inFO) {
    return Routes.ably_token_path({ type: tokenType, format: 'json' });
  }

  // ably is not available in PO for the moment
  return null;
};

export const useAblyAvailable: UseAblyAvailable = (enabled = true) => {
  const authUrl = useAblyAuthUrl('frontoffice');

  return enabled && authUrl !== null;
};

export const useSubscribeToAblyChannel = ({
  tokenType,
  channel,
  onMessage,
  enabled = true,
  useHistory = false,
}: UseSubscribeToAblyChannelParams) => {
  const authUrl = useAblyAuthUrl(tokenType);
  const ablyAvailable = useAblyAvailable(enabled);

  const ably = useRef<Realtime | null>(null);

  const [ablyReady, setAblyReady] = useState(
    ably.current?.connection.state === 'connected',
  );

  // Configure the connection if the flag is enabled
  useEffect(() => {
    if (ablyAvailable && authUrl && ably.current === null) {
      ably.current = configureAbly({
        authUrl,
        environment: clusterToEnv(window.__HB_ENV__.CLUSTER_NAME),
        log: { level: process.env.NODE_ENV === 'production' ? 1 : 3 },
      });
      ably.current.connection.on('connected', () => {
        setAblyReady(true);
      });
    }

    if (ably.current?.connection.state === 'connected') {
      setAblyReady(true);
    }
  }, [ablyAvailable, authUrl]);

  // Subscribe to the channel once the connection is ready
  useEffect(() => {
    if (ablyAvailable && ably.current && ablyReady) {
      const ablyChannel = ably.current.channels.get(channel);

      ablyChannel.subscribe(onMessage);

      // History is used whenever the channel subscribers might've missed a published message.
      if (useHistory) {
        ablyChannel
          .history({ limit: 20 })
          .then((page) => {
            // Process each historical message
            page.items.reverse().forEach(onMessage);
          })
          .catch((err) => {
            if (err) {
              captureException(err);
            }
          });
      }

      return () => {
        ably.current?.channels.get(channel).unsubscribe(onMessage);
      };
    }
  }, [ablyReady, channel, ablyAvailable, onMessage]);

  // Close the connection if the flag gets disabled
  useEffect(() => {
    if (!ablyAvailable && ably.current) {
      ably.current.close();
    }
  }, [ablyAvailable]);
};
